import { ArtifactFormSkeleton } from "@/components/layout/artifact-form-skeleton"
import { useState, useEffect, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { API_ORIGIN, isPersonPrefillEnabled } from "@/constants"
import { OnboardingStep } from "@/hooks/use-onboarding"
import { useProfile } from "@/hooks/use-profile"
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import { PersonPrefillSuggestions } from '@/components/onboarding/person-prefill-suggestions'
import { FormField } from '@/components/ui/form-field'
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { CountrySelect } from "@/components/country-select"
import { CLIENT_LOCALE } from "@/lib/client-locale"
import { entityCountryForEdit } from "@/utils/entity-location-fields"
import { OnboardingStepNext, runOnboardingStepNext } from "@/lib/onboarding-step-nav"
import {
  acceptWrites,
  continueBasicInfoPayload,
  defaultListKeep,
  dropPersonPrefillDraft,
  PERSON_PREFILL_DRAFT_QUERY_KEY,
  postPersonPrefillAccept,
  prefillReviewForm,
  savedReviewForm,
  type PersonPrefillDraftResponse,
  type PersonPrefillListKey,
  type PersonPrefillScalarKey,
  type PersonReviewForm,
} from "@/lib/person-prefill"

interface ProfileReviewStepProps {
  step: OnboardingStep
  onNext: OnboardingStepNext
  onBack?: () => void
  isCompleting: boolean
  canGoBack: boolean
}

const EXTRA_SCALAR_FIELDS: { key: PersonPrefillScalarKey; label: string; id: string }[] = [
  { key: 'first_name', label: 'First name', id: 'profile-review-first-name' },
  { key: 'last_name', label: 'Last name', id: 'profile-review-last-name' },
  { key: 'website', label: 'Website', id: 'profile-review-website' },
  { key: 'linkedin_url', label: 'LinkedIn', id: 'profile-review-linkedin' },
  { key: 'x_url', label: 'X', id: 'profile-review-x' },
]

export function ProfileReviewStep({ onNext, onBack, canGoBack }: ProfileReviewStepProps) {
  const { profile, isLoading } = useProfile()
  const queryClient = useQueryClient()
  const prefillEnabled = isPersonPrefillEnabled()
  const personDraftResponse = prefillEnabled
    ? queryClient.getQueryData<PersonPrefillDraftResponse>(PERSON_PREFILL_DRAFT_QUERY_KEY)
    : undefined
  const personDraft =
    personDraftResponse?.written === false ? personDraftResponse.draft : undefined

  const [headline, setHeadline] = useState("")
  const [bio, setBio] = useState("")
  const [city, setCity] = useState("")
  const [country, setCountry] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [website, setWebsite] = useState("")
  const [linkedinUrl, setLinkedinUrl] = useState("")
  const [xUrl, setXUrl] = useState("")
  const [suggestions, setSuggestions] = useState<Partial<Record<PersonPrefillScalarKey, string>>>({})
  const [listKeep, setListKeep] = useState<Partial<Record<PersonPrefillListKey, boolean>>>({})
  const [accepted, setAccepted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hydrated = useRef(false)

  useEffect(() => {
    if (!profile || prefillEnabled) return
    setHeadline(profile.headline || "")
    setBio(profile.bio || "")
    setCity(profile.city || "")
    setCountry(entityCountryForEdit(profile.country, CLIENT_LOCALE))
  }, [profile, prefillEnabled])

  useEffect(() => {
    if (!profile || !prefillEnabled || hydrated.current) return
    hydrated.current = true
    const saved = savedReviewForm(profile)
    if (!personDraft) {
      setHeadline(saved.headline)
      setBio(saved.bio)
      setCity(saved.city)
      setCountry(entityCountryForEdit(saved.country, CLIENT_LOCALE))
      setFirstName(saved.first_name)
      setLastName(saved.last_name)
      setWebsite(saved.website)
      setLinkedinUrl(saved.linkedin_url)
      setXUrl(saved.x_url)
      return
    }
    const prefilled = prefillReviewForm(profile, personDraft)
    const countryValue = entityCountryForEdit(prefilled.form.country, CLIENT_LOCALE)
    const displaySuggestions = { ...prefilled.suggestions }
    if (displaySuggestions.country) {
      if (countryValue) displaySuggestions.country = countryValue
      else delete displaySuggestions.country
    }
    setHeadline(prefilled.form.headline)
    setBio(prefilled.form.bio)
    setCity(prefilled.form.city)
    setCountry(countryValue)
    setFirstName(prefilled.form.first_name)
    setLastName(prefilled.form.last_name)
    setWebsite(prefilled.form.website)
    setLinkedinUrl(prefilled.form.linkedin_url)
    setXUrl(prefilled.form.x_url)
    setSuggestions(displaySuggestions)
    setListKeep(defaultListKeep(personDraft))
  }, [profile, prefillEnabled, personDraft])

  const reviewForm = (): PersonReviewForm => ({
    first_name: firstName,
    last_name: lastName,
    headline,
    bio,
    city,
    country,
    website,
    linkedin_url: linkedinUrl,
    x_url: xUrl,
  })

  const applySavedProfile = () => {
    if (!profile) return
    const saved = savedReviewForm(profile)
    setHeadline(saved.headline)
    setBio(saved.bio)
    setCity(saved.city)
    setCountry(entityCountryForEdit(saved.country, CLIENT_LOCALE))
    setFirstName(saved.first_name)
    setLastName(saved.last_name)
    setWebsite(saved.website)
    setLinkedinUrl(saved.linkedin_url)
    setXUrl(saved.x_url)
    setSuggestions({})
    setListKeep({})
  }

  const handleDiscard = () => {
    dropPersonPrefillDraft(queryClient)
    setAccepted(false)
    setError(null)
    applySavedProfile()
  }

  const handleAccept = async () => {
    if (!profile || !personDraft) return
    setAccepting(true)
    setError(null)
    try {
      const writes = acceptWrites(profile, reviewForm(), personDraft, listKeep)
      await postPersonPrefillAccept(writes)
      dropPersonPrefillDraft(queryClient)
      setAccepted(true)
      void queryClient.invalidateQueries({ queryKey: ['profile'] })
    } catch (err) {
      console.error('Error accepting LinkedIn draft:', err)
      setError(err instanceof Error ? err.message : 'Could not save the accepted fields.')
    } finally {
      setAccepting(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const token = localStorage.getItem('auth_token')
      const body = prefillEnabled
        ? continueBasicInfoPayload({
            prefillEnabled: true,
            accepted,
            saved: profile ?? {},
            form: reviewForm(),
            suggestions,
          })
        : {
            first_name: profile?.first_name || 'User',
            last_name: profile?.last_name || '',
            headline: headline || null,
            bio: bio || null,
            city: city || null,
            country: country || null,
          }
      const response = await fetch(`${API_ORIGIN}/api/profiles/onboarding/basic-info`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      if (response.ok) {
        await runOnboardingStepNext(onNext, setError)
      } else {
        const errorData = await response.json().catch(() => ({}))
        setError(errorData.error || 'Failed to save profile')
      }
    } catch (err) {
      console.error('Error saving profile:', err)
      setError('Network error. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const setExtraField = (key: PersonPrefillScalarKey, value: string) => {
    if (key === 'first_name') setFirstName(value)
    if (key === 'last_name') setLastName(value)
    if (key === 'website') setWebsite(value)
    if (key === 'linkedin_url') setLinkedinUrl(value)
    if (key === 'x_url') setXUrl(value)
  }

  const extraValue = (key: PersonPrefillScalarKey): string => {
    if (key === 'first_name') return firstName
    if (key === 'last_name') return lastName
    if (key === 'website') return website
    if (key === 'linkedin_url') return linkedinUrl
    return xUrl
  }

  if (isLoading) {
    return <ArtifactFormSkeleton fields={4} ariaLabel="Loading profile" />
  }

  const showDraft = Boolean(personDraft) && !accepted
  const busy = saving || accepting

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {prefillEnabled && showDraft ? (
          <PersonPrefillSuggestions
            enabled
            draft={personDraft}
            keep={listKeep}
            onKeepChange={(key, checked) => setListKeep((current) => ({ ...current, [key]: checked }))}
            busy={busy}
          />
        ) : null}

        {EXTRA_SCALAR_FIELDS.map((field) =>
          prefillEnabled && suggestions[field.key] ? (
            <FormField key={field.key} label={field.label} htmlFor={field.id}>
              <Input
                id={field.id}
                value={extraValue(field.key)}
                onChange={(event) => setExtraField(field.key, event.target.value)}
                disabled={busy}
              />
            </FormField>
          ) : null,
        )}

        <FormField label="Current or target role" htmlFor="profile-review-headline">
          <Input
            id="profile-review-headline"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            disabled={busy}
            placeholder="e.g. Senior Engineer"
          />
        </FormField>

        <FormField label="Bio" htmlFor="bio">
          <Textarea
            id="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            disabled={busy}
            rows={4}
          />
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="City" htmlFor="city">
            <Input
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={busy}
            />
          </FormField>
          <FormField label="Country" htmlFor="country">
            <CountrySelect
              id="country"
              value={country}
              onValueChange={setCountry}
              disabled={busy}
            />
          </FormField>
        </div>
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <OnboardingStepActions
        canGoBack={canGoBack}
        onBack={onBack}
        onContinue={handleSave}
        continueDisabled={busy}
        isBusy={saving}
        trailing={
          showDraft ? (
            <>
              <Button type="button" variant="outline" onClick={handleDiscard} disabled={busy}>
                Discard
              </Button>
              <Button type="button" variant="outline" onClick={() => void handleAccept()} disabled={busy}>
                {accepting ? 'Saving…' : 'Accept'}
              </Button>
            </>
          ) : null
        }
      />
    </div>
  )
}
